/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

export function MarkupCalculatorClient() {
  const [cost, setCost] = useState("");
  const [markupPercent, setMarkupPercent] = useState("");
  const [mode, setMode] = useState<"markup" | "margin">("markup");

  const cIn = cost;
  const pIn = markupPercent;
  const costNum = parseFloat(cIn) || 0;
  const percentNum = parseFloat(pIn) || 0;

  let sellingPrice = 0;
  let profit = 0;
  let markupResult = 0;
  let marginResult = 0;

  if (costNum > 0 && percentNum > 0) {
    if (mode === "markup") {
      sellingPrice = costNum * (1 + percentNum / 100);
      profit = sellingPrice - costNum;
      markupResult = percentNum;
      marginResult = (profit / sellingPrice) * 100;
    } else {
      sellingPrice = costNum / (1 - percentNum / 100);
      profit = sellingPrice - costNum;
      markupResult = (profit / costNum) * 100;
      marginResult = percentNum;
    }
  }

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setMode("markup")}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            mode === "markup"
              ? "bg-foreground text-background"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          From Markup %
        </button>
        <button
          onClick={() => setMode("margin")}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            mode === "margin"
              ? "bg-foreground text-background"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          From Margin %
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-foreground">
            Job Cost ($)
          </label>
          <input
            type="number"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="10000"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            {mode === "markup" ? "Markup %" : "Margin %"}
          </label>
          <input
            type="number"
            value={markupPercent}
            onChange={(e) => setMarkupPercent(e.target.value)}
            placeholder={mode === "markup" ? "35" : "25"}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
      </div>

      {costNum > 0 && percentNum > 0 && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Selling Price</div>
              <div className="text-2xl font-bold text-foreground">
                ${fmt(sellingPrice)}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Profit</div>
              <div className="text-2xl font-bold text-secondary">
                ${fmt(profit)}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Markup</div>
              <div className="text-2xl font-bold text-foreground">
                {fmt(markupResult)}%
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Margin</div>
              <div className="text-2xl font-bold text-foreground">
                {fmt(marginResult)}%
              </div>
            </div>
          </div>

          <ResultActions
            toolSlug="markup-calculator"
            inputs={{ cost, markupPercent, mode }}
            outputs={{ sellingPrice, profit, markupResult, marginResult }}
          />
        </>
      )}
    </div>
  );
}
